/**
 * InvoicePaidNotifier — global "cha-ching" listener.
 *
 * Mounts once at the App level (authenticated branch). Listens for
 * `street_legal.invoice_paid` WebSocket events and surfaces them as:
 *   • Toast with dealer name + amount
 *   • Two-tone soft chime via Web Audio API (no audio asset, no
 *     network fetch — generated in-browser)
 *
 * Duplicate suppression: ignores the same invoice_id if it fires
 * within 10s (idempotent webhook can re-deliver).
 *
 * iter 370hf.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { formatCents } from "./moneyUtils";

// Soft two-tone "cha-ching" — sine wave at 880Hz then 1320Hz (a
// perfect fifth). Quiet by default; users on muted devices won't
// hear anything but the toast still appears.
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    const tone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      // Quick attack + exponential decay so it sounds like a soft
      // bell, not a beep.
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.15, now + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };

    tone(880, 0, 0.18);   // A5
    tone(1320, 0.14, 0.32); // E6 (perfect fifth above)

    // Close the context shortly after the last tone finishes so we
    // don't leak audio resources.
    setTimeout(() => {
      try { ctx.close(); } catch { /* noop */ }
    }, 700);
  } catch {
    // Autoplay restrictions, no AudioContext, etc. — toast still
    // fires regardless. Silent failure is correct here.
  }
}

export function InvoicePaidNotifier() {
  // Track recently-shown invoice ids to suppress duplicate webhook
  // re-deliveries within a 10s window.
  const recentRef = useRef(new Map());

  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (!msg || msg.event !== "street_legal.invoice_paid") return;
      const payload = msg.data || {};
      const id = payload.invoice_id;
      if (!id) return;

      // Suppress dupes (webhook is idempotent so the same event can
      // arrive twice within a few hundred ms during failover).
      const now = Date.now();
      const lastSeen = recentRef.current.get(id);
      if (lastSeen && now - lastSeen < 10_000) return;
      recentRef.current.set(id, now);
      // Light-touch GC so the map doesn't grow unbounded.
      if (recentRef.current.size > 50) {
        const cutoff = now - 60_000;
        for (const [k, t] of recentRef.current) {
          if (t < cutoff) recentRef.current.delete(k);
        }
      }

      const dealer = payload.dealer_name || "Dealer";
      const amount = formatCents(payload.amount_cents || 0);

      playChime();
      toast.success(`${dealer} paid ${amount}`, {
        description: "Street Legal invoice marked paid.",
        icon: <CheckCircle2 className="h-4 w-4 text-emerald-400" />,
        duration: 6000,
      });
    };

    window.addEventListener("ws_event", handler);
    return () => window.removeEventListener("ws_event", handler);
  }, []);

  return null;
}
